import React, { useState } from 'react'
import { CircularProgress } from '@mui/material'
import {
  useGetFollowerAnalyticsQuery,
  Platform,
  useEnableFollowerTrackingMutation,
  useSyncFollowerCountsTrackingMutation,
  useSyncFollowerTrackingAccountsMutation,
} from 'graphql/generated/graphql'
import { useUser } from 'hooks/useUser'
import {
  Users,
  TrendingUp,
  TrendingDown,
  Activity,
  Instagram,
  Youtube,
  RefreshCw,
  Calendar,
  UserPlus,
  UserMinus,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts'

const FollowerAnalytics: React.FC = () => {
  const { user } = useUser()

  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d')
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | 'all'>('all')

  const { data, loading, error, refetch } = useGetFollowerAnalyticsQuery({
    variables: { timeRange },
    skip: !user,
    pollInterval: 300000, // Refresh every 5 minutes
  })

  const [enableTracking, { loading: enablingTracking }] = useEnableFollowerTrackingMutation()
  const [syncCounts, { loading: syncingCounts }] = useSyncFollowerCountsTrackingMutation()
  const [syncAccounts, { loading: syncingAccounts }] = useSyncFollowerTrackingAccountsMutation()

  const handleEnableTracking = async () => {
    try {
      await enableTracking({
        variables: {
          snapshotFrequency: 1440, // 24 hours
          retentionDays: 90,
        },
      })
      await refetch()
      alert('Follower tracking enabled! Data will appear within 24 hours.')
    } catch (err: unknown) {
      const error = err as Error
      alert(`Error enabling tracking: ${error.message}`)
    }
  }

  const handleSyncCounts = async () => {
    try {
      await syncCounts()
      await refetch()
      alert('Follower counts synced!')
    } catch (err: unknown) {
      const error = err as Error
      alert(`Error syncing: ${error.message}`)
    }
  }

  const handleSyncAccounts = async () => {
    try {
      const result = await syncAccounts()
      const data = result.data?.syncFollowerTrackingAccounts
      if (data) {
        const parts = []
        if (data.added.length > 0) {
          parts.push(`Added: ${data.added.join(', ')}`)
        }
        if (data.removed.length > 0) {
          parts.push(`Removed: ${data.removed.join(', ')}`)
        }
        if (parts.length === 0) {
          parts.push('All accounts already in sync')
        }
        alert(`Sync complete!\n\n${parts.join('\n')}`)
      }
      await refetch()
    } catch (err: unknown) {
      const error = err as Error
      alert(`Error syncing accounts: ${error.message}`)
    }
  }

  const getPlatformIcon = (platform: Platform) => {
    switch (platform) {
      case Platform.Instagram:
        return <Instagram className="w-5 h-5" />
      case Platform.TikTok:
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
          </svg>
        )
      case Platform.Youtube:
        return <Youtube className="w-5 h-5" />
      default:
        return <Users className="w-5 h-5" />
    }
  }

  const getPlatformColor = (platform: Platform) => {
    switch (platform) {
      case Platform.Instagram:
        return '#E4405F'
      case Platform.TikTok:
        return '#00f2ea'
      case Platform.Youtube:
        return '#FF0000'
      default:
        return '#a855f7'
    }
  }

  const filterByPlatform = <T extends { platform: Platform }>(items: T[]): T[] => {
    if (selectedPlatform === 'all') return items
    return items.filter((item) => item.platform === selectedPlatform)
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-300">Please log in to view follower analytics</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <CircularProgress sx={{ color: '#a855f7' }} />
          <p className="text-gray-300 mt-4">Loading follower analytics...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">Error loading analytics: {error.message}</p>
          <button
            onClick={() => {
              refetch().catch(console.error)
            }}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Define analytics before using it
  const analytics = data?.getFollowerAnalytics

  // Check if tracking is not enabled (no accounts)
  if (!analytics?.accounts || analytics.accounts.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="mb-6 bg-slate-800/50 backdrop-blur-xl border border-purple-500/20 rounded-2xl p-8">
            <Users className="w-16 h-16 text-purple-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">
              Follower Tracking Not Enabled
            </h2>
            <p className="text-gray-400">
              Enable follower tracking to see who follows and unfollows you across Instagram, TikTok, and YouTube.
            </p>
          </div>
          
          <button
            onClick={() => { void handleEnableTracking() }}
            disabled={enablingTracking}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-purple-500/50"
          >
            {enablingTracking ? (
              <span className="flex items-center gap-2 justify-center">
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Enabling...
              </span>
            ) : (
              'Enable Follower Tracking'
            )}
          </button>
          
          <p className="text-sm text-gray-500 mt-4">
            First snapshot will be taken within 24 hours
          </p>
        </div>
      </div>
    )
  }

  const accounts = filterByPlatform(analytics.accounts)
  const snapshots = filterByPlatform(analytics.snapshots ?? [])
  const recentFollowers = filterByPlatform(analytics.recentFollowers ?? [])
  const recentUnfollowers = filterByPlatform(analytics.recentUnfollowers ?? [])
  const summary = analytics.summary ?? {
    totalFollowers: 0,
    totalGained: 0,
    totalLost: 0,
    netChange: 0,
    growthRate: 0,
  }

  // Prepare chart data
  const chartData = [...snapshots]
    .sort((a, b) => new Date(a.snapshotAt).getTime() - new Date(b.snapshotAt).getTime())
    .map((snapshot) => ({
      date: new Date(snapshot.snapshotAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
      followers: snapshot.followerCount,
      gained: snapshot.newFollowers,
      lost: snapshot.unfollowers,
      netChange: snapshot.netChange,
      platform: snapshot.platform,
    }))

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Follower Analytics
            </h1>
            <p className="text-gray-400 mt-2">Track your follower growth across platforms</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { void handleSyncAccounts() }}
              disabled={syncingAccounts}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 backdrop-blur-xl border border-purple-500/20 rounded-lg hover:bg-slate-800/80 transition-all text-gray-300 hover:text-white disabled:opacity-50"
              title="Sync with connected accounts"
            >
              <RefreshCw className={`w-4 h-4 ${syncingAccounts ? 'animate-spin' : ''}`} />
              Sync Accounts
            </button>
            <button
              onClick={() => {
                refetch().catch(console.error)
              }}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 backdrop-blur-xl border border-purple-500/20 rounded-lg hover:bg-slate-800/80 transition-all text-gray-300 hover:text-white"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 flex-wrap">
          {/* Time Range Filter */}
          <div className="flex gap-2">
            {['7d', '30d', '90d', 'all'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range as any)}
                className={`px-4 py-2 rounded-lg transition-all ${
                  timeRange === range
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/50'
                    : 'bg-slate-800/50 backdrop-blur-xl border border-purple-500/20 text-gray-300 hover:bg-slate-800/80 hover:text-white'
                }`}
              >
                {range === 'all' ? 'All Time' : range.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Platform Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedPlatform('all')}
              className={`px-4 py-2 rounded-lg transition-all ${
                selectedPlatform === 'all'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/50'
                  : 'bg-slate-800/50 backdrop-blur-xl border border-purple-500/20 text-gray-300 hover:bg-slate-800/80 hover:text-white'
              }`}
            >
              All Platforms
            </button>
            {[Platform.Instagram, Platform.TikTok, Platform.Youtube].map((platform) => (
              <button
                key={platform}
                onClick={() => setSelectedPlatform(platform)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  selectedPlatform === platform
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/50'
                    : 'bg-slate-800/50 backdrop-blur-xl border border-purple-500/20 text-gray-300 hover:bg-slate-800/80 hover:text-white'
                }`}
              >
                {getPlatformIcon(platform)}
                {platform}
              </button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total Followers */}
          <div className="bg-slate-800/50 backdrop-blur-xl border border-purple-500/20 rounded-2xl p-6 hover:bg-slate-800/80 hover:shadow-lg hover:shadow-purple-500/20 transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shadow-lg shadow-purple-500/50">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-400">Total Followers</p>
                <p className="text-3xl font-bold text-white">{summary.totalFollowers.toLocaleString()}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              {summary.netChange >= 0 ? (
                <div className="flex items-center text-green-400">
                  <ArrowUp className="w-4 h-4" />
                  <span className="font-semibold">{Math.abs(summary.netChange)}</span>
                </div>
              ) : (
                <div className="flex items-center text-red-400">
                  <ArrowDown className="w-4 h-4" />
                  <span className="font-semibold">{Math.abs(summary.netChange)}</span>
                </div>
              )}
              <span className="text-gray-500">net change</span>
            </div>
          </div>

          {/* Followers Gained */}
          <div className="bg-slate-800/50 backdrop-blur-xl border border-green-500/20 rounded-2xl p-6 hover:bg-slate-800/80 hover:shadow-lg hover:shadow-green-500/20 transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl shadow-lg shadow-green-500/50">
                <UserPlus className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-400">Followers Gained</p>
                <p className="text-3xl font-bold text-white">{summary.totalGained.toLocaleString()}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <TrendingUp className="w-4 h-4 text-green-400" />
              <span className="text-gray-500">{timeRange === 'all' ? 'All time' : `Last ${timeRange}`}</span>
            </div>
          </div>

          {/* Followers Lost */}
          <div className="bg-slate-800/50 backdrop-blur-xl border border-red-500/20 rounded-2xl p-6 hover:bg-slate-800/80 hover:shadow-lg hover:shadow-red-500/20 transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-red-500 to-rose-500 rounded-xl shadow-lg shadow-red-500/50">
                <UserMinus className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-400">Followers Lost</p>
                <p className="text-3xl font-bold text-white">{summary.totalLost.toLocaleString()}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <TrendingDown className="w-4 h-4 text-red-400" />
              <span className="text-gray-500">{timeRange === 'all' ? 'All time' : `Last ${timeRange}`}</span>
            </div>
          </div>

          {/* Growth Rate */}
          <div className="bg-slate-800/50 backdrop-blur-xl border border-blue-500/20 rounded-2xl p-6 hover:bg-slate-800/80 hover:shadow-lg hover:shadow-blue-500/20 transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl shadow-lg shadow-blue-500/50">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-400">Growth Rate</p>
                <p className="text-3xl font-bold text-white">{summary.growthRate.toFixed(1)}%</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              {summary.growthRate >= 0 ? (
                <div className="flex items-center text-green-400">
                  <TrendingUp className="w-4 h-4" />
                  <span>Growing</span>
                </div>
              ) : (
                <div className="flex items-center text-red-400">
                  <TrendingDown className="w-4 h-4" />
                  <span>Declining</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Account Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {accounts.map((account) => (
            <div
              key={account.id}
              className="bg-slate-800/50 backdrop-blur-xl border border-purple-500/20 rounded-2xl p-6 hover:bg-slate-800/80 hover:shadow-lg hover:shadow-purple-500/20 transition-all"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="p-2 rounded-lg"
                    style={{ 
                      backgroundColor: `${getPlatformColor(account.platform)}20`,
                      color: getPlatformColor(account.platform)
                    }}
                  >
                    {getPlatformIcon(account.platform)}
                  </div>
                  <div>
                    <p className="font-semibold text-white">{account.displayName ?? account.username}</p>
                    <p className="text-sm text-gray-400">@{account.username}</p>
                  </div>
                </div>
                {account.isActive ? (
                  <div className="w-2 h-2 bg-green-500 rounded-full shadow-lg shadow-green-500/50"></div>
                ) : (
                  <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-400">Current Followers</span>
                  <span className="font-semibold text-white">
                    {account.currentFollowerCount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-400">Total Gained</span>
                  <span className="font-semibold text-green-400">+{account.totalNewFollows}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-400">Total Lost</span>
                  <span className="font-semibold text-red-400">-{account.totalUnfollows}</span>
                </div>
              </div>

              {account.lastSnapshotAt && (
                <div className="mt-4 pt-4 border-t border-purple-500/20">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Calendar className="w-3 h-3" />
                    Last updated: {new Date(account.lastSnapshotAt).toLocaleDateString()}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Follower Growth Chart */}
          <div className="bg-slate-800/50 backdrop-blur-xl border border-purple-500/20 rounded-2xl p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Follower Growth Over Time</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorFollowers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#a855f7" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(30, 41, 59, 0.95)',
                    border: '1px solid rgba(168, 85, 247, 0.2)',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="followers"
                  stroke="#a855f7"
                  strokeWidth={2}
                  fill="url(#colorFollowers)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Gained vs Lost Chart */}
          <div className="bg-slate-800/50 backdrop-blur-xl border border-purple-500/20 rounded-2xl p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Gained vs Lost</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(30, 41, 59, 0.95)',
                    border: '1px solid rgba(168, 85, 247, 0.2)',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                />
                <Legend />
                <Bar dataKey="gained" fill="#10b981" name="Gained" radius={[8, 8, 0, 0]} />
                <Bar dataKey="lost" fill="#ef4444" name="Lost" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Follow/unfollow tracking removed — data unreliable */}
      </div>
    </div>
  )
}

export default FollowerAnalytics